import { Link } from 'react-router-dom';
import styled from 'styled-components';

/**
 * Стили окна задачи (ТЗ 0064).
 *
 * Отдельный модуль, а не добавка к `TaskBoard.styled.ts`: тот и без того на
 * 950 строк и описывает доску, а это — карточка. Общие примитивы (`Chip`,
 * `EditBtn`, `DrawerLabel`) по-прежнему берутся оттуда: заводить их вторые
 * экземпляры значило бы получить два слегка разных чипа на одном экране.
 */

/**
 * Полотно окна: работа слева, свойства справа (ТЗ 0065).
 *
 * 🔴 Вкладок здесь БОЛЬШЕ НЕТ. Они прятали ровно то, ради чего окно
 * открывают: чтобы посмотреть, кто исполнитель и с какой группой связана
 * задача, приходилось переключаться туда и обратно, теряя из виду описание.
 * Аудит назвал это «административной формой», и он прав: форма спрашивает,
 * рабочее место — показывает.
 *
 * Вместе с вкладками ушла и подпорка `hidden=` вместо условного рендера:
 * она существовала только затем, чтобы черновик реплики и выбранные, но ещё
 * не прицепленные связи переживали переключение. Переключать больше нечего.
 */
export const Canvas = styled.div`
  display: grid;
  /* minmax(0, …) обязателен: без него длинное имя исполнителя или связи
     распирает колонку и ломает сетку. */
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 24px;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 16px;
  }
`;

export const MainCol = styled.div`
  min-width: 0;
`;

/** Колонка свойств. Своя рамка и фон — она читается как «паспорт задачи»,
 *  а не как продолжение описания. */
export const SideCol = styled.aside`
  min-width: 0;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #f8fafc;
  padding: 14px;
`;

/**
 * Заголовок окна: чекбокс, название на месте, метки «AI» и несохранённого.
 *
 * Справа — место под крестик antd (`.ant-modal-close`, 56px поверх правого
 * поля шапки 24px): без него метка в конце строки уезжала под крестик.
 */
export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  padding-right: 40px;
`;

export const TitleCheck = styled.input`
  width: 17px;
  height: 17px;
  margin: 0;
  flex-shrink: 0;
  cursor: pointer;
  accent-color: #2563eb;
`;

/** Название правится ПРЯМО В ШАПКЕ. Отдельное поле «Название» внутри формы
 *  дублировало заголовок окна: одна и та же строка стояла на экране дважды. */
export const TitleInput = styled.input<{ $done?: boolean }>`
  flex: 1;
  min-width: 0;
  border: 1px solid transparent;
  border-radius: 6px;
  background: transparent;
  padding: 3px 8px;
  font: inherit;
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
  text-decoration: ${({ $done }) => ($done ? 'line-through' : 'none')};

  &:hover {
    border-color: #e2e8f0;
  }

  &:focus {
    outline: none;
    border-color: #2563eb;
    background: #fff;
  }
`;

/** Одно свойство в правой колонке. */
export const Prop = styled.div`
  margin-bottom: 14px;

  &:last-child {
    margin-bottom: 0;
  }
`;

/**
 * Сегменты вместо выпадающего списка.
 *
 * Статус и приоритет меняют чаще всего остального, а `<select>` стоит трёх
 * действий: открыть, найти, выбрать. Сетка, а не строка: «Запланировано» и
 * «Отменена» в одну линию на 320px не помещаются, и колонка ехала бы вширь.
 */
export const SegGroup = styled.div<{ $cols: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $cols }) => $cols}, minmax(0, 1fr));
  gap: 3px;
  background: #f1f5f9;
  border-radius: 8px;
  padding: 3px;
`;

export const Seg = styled.button<{ $active: boolean }>`
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-width: 0;
  border: 0;
  border-radius: 6px;
  padding: 5px 6px;
  font: inherit;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  background: ${({ $active }) => ($active ? '#fff' : 'transparent')};
  color: ${({ $active }) => ($active ? '#0f172a' : '#64748b')};
  box-shadow: ${({ $active }) =>
    $active ? '0 1px 3px rgba(0, 0, 0, 0.08)' : 'none'};
  transition: background 0.15s, color 0.15s;

  &:hover:not(:disabled) {
    color: #0f172a;
  }

  &:disabled {
    cursor: default;
  }

  svg {
    display: block;
    flex-shrink: 0;
  }
`;

/** Строка опции селекта исполнителя: аватар и имя. */
export const OptionRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

export const SideDivider = styled.div`
  height: 1px;
  background: #e2e8f0;
  margin: 14px -14px;
`;

export const SideHead = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #475569;
  margin-bottom: 10px;
`;

/** Свёрнутая история изменений. Своя, а не общий `Disclosure`: тот помнит
 *  раскрытие в `localStorage` одним ключом на все задачи и рисует значок
 *  «инфо», который здесь врал бы про содержимое. */
export const FoldToggle = styled.button`
  appearance: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 0;
  background: none;
  padding: 0;
  margin-top: 8px;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  color: #2563eb;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  svg {
    display: block;
  }
`;

/** Счётчик у заголовка секции — связей, реплик. */
export const TabCount = styled.span`
  min-width: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: #e2e8f0;
  color: #475569;
  font-size: 11px;
  font-weight: 600;
  line-height: 18px;
  text-align: center;
`;

/** Две колонки полей окна создания. Не три: «Запланировано» и три
 *  приоритета в трети от 860px не помещаются. */
export const CreateGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 16px;

  @media (max-width: 720px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

/** Две даты рядом: начало и срок читаются парой. */
export const DateGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
`;

export const Section = styled.div`
  margin-bottom: 18px;
`;

/** Строка «есть несохранённое» + кнопки. Прижата книзу окна, а не плавает
 *  внутри прокрутки: иначе о ней узнают, только долистав. */
export const Footer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  justify-content: flex-end;
`;

export const FooterNote = styled.span<{ $tone?: 'warn' | 'danger' }>`
  margin-right: auto;
  font-size: 12px;
  color: ${({ $tone }) =>
    // eslint-disable-next-line no-nested-ternary
    $tone === 'danger' ? '#b91c1c' : $tone === 'warn' ? '#8a6100' : '#64748b'};
`;

/* ── Связи ───────────────────────────────────────────────────────────────── */

export const LinkGroup = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px 12px;
  margin-bottom: 10px;
  background: #f8fafc;
`;

export const LinkGroupHead = styled.button`
  appearance: none;
  border: 0;
  background: none;
  cursor: pointer;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  color: #334155;
  letter-spacing: 0.02em;
`;

export const LinkRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0 6px 22px;
  border-top: 1px solid #eef2f7;
  min-width: 0;
`;

/** Кликается ИМЯ, а не вся строка: рядом живёт крестик, и промах по строке
 *  не должен уводить со страницы. */
export const LinkName = styled.a`
  color: #0f172a;
  font-size: 13px;
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    color: #2563eb;
    text-decoration: underline;
  }
`;

/** Та же ссылка, но роутером — связь, чей адрес собрал сервер (ТЗ 0088). */
export const LinkNameRoute = styled(Link)`
  color: #0f172a;
  font-size: 13px;
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    color: #2563eb;
    text-decoration: underline;
  }
`;

/** Связь, которую этому человеку не открыть (раздел закрыт): приглушённая
 *  подпись без ссылки, а не «битая» ссылка (ТЗ 0088). */
export const LinkNameMuted = styled.span`
  color: #64748b;
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const LinkNamePlain = styled.span`
  color: #0f172a;
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const LinkHint = styled.span`
  color: #64748b;
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const LinkMissing = styled.span`
  color: #b91c1c;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
`;

export const LinkRemove = styled.button`
  appearance: none;
  border: 0;
  background: none;
  cursor: pointer;
  margin-left: auto;
  padding: 2px;
  border-radius: 6px;
  color: #94a3b8;
  display: inline-flex;

  &:hover {
    color: #b91c1c;
    background: #fee2e2;
  }
`;

/** «Показать ещё N» внутри группы связей — текстовая кнопка, а не ссылка:
 *  она ничего не открывает, только разворачивает список. */
export const MoreButton = styled.button`
  appearance: none;
  border: 0;
  background: none;
  cursor: pointer;
  padding: 6px 0 0 22px;
  font: inherit;
  font-size: 12px;
  color: #2563eb;

  &:hover {
    text-decoration: underline;
  }
`;

export const AddLinkRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  flex-wrap: wrap;
  margin-top: 12px;
`;

/* ── Активность ──────────────────────────────────────────────────────────── */

export const Comment = styled.div`
  border-top: 1px solid #eef2f7;
  padding: 10px 0;
`;

export const CommentHead = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 4px;
`;

export const CommentAuthor = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
`;

export const CommentTime = styled.span`
  font-size: 11px;
  color: #94a3b8;
`;

export const CommentBody = styled.div`
  font-size: 13px;
  color: #1e293b;
  white-space: pre-wrap;
  word-break: break-word;
`;

export const CommentActions = styled.div`
  display: flex;
  gap: 8px;
  margin-left: auto;
`;

export const Milestone = styled.div`
  display: flex;
  gap: 8px;
  align-items: baseline;
  font-size: 12px;
  color: #64748b;
  padding: 4px 0;
`;

export const Empty = styled.div`
  color: #94a3b8;
  font-size: 13px;
  padding: 10px 0;
`;

/* ── Описание разметкой и её редактор (ТЗ 0066) ──────────────────────────── */

/**
 * Показ описания. Оформление списков, кода и цитат повторяет то, что уже
 * применяется к телам сообщений коммуникатора: разметка в ERP должна
 * выглядеть одинаково, где бы её ни показывали.
 *
 * white-space: pre-line — одиночные переводы строк остаются видимыми (в
 * markdown это мягкий перенос), а подряд идущие пробелы схлопываются.
 */
export const Markdown = styled.div`
  font-size: 14px;
  line-height: 1.55;
  color: #1e293b;
  white-space: pre-line;
  word-break: break-word;
  overflow-wrap: anywhere;

  p {
    margin: 0;
  }

  p + p,
  ul,
  ol,
  pre,
  blockquote,
  table {
    margin-top: 8px;
  }

  ul,
  ol {
    padding-left: 20px;
    white-space: normal;
  }

  a {
    color: #2563eb;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  code {
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
    font-size: 0.92em;
    background: rgba(15, 23, 42, 0.06);
    border-radius: 4px;
    padding: 1px 4px;
  }

  pre {
    background: rgba(15, 23, 42, 0.06);
    border-radius: 6px;
    padding: 8px 10px;
    overflow-x: auto;
    white-space: pre;

    code {
      background: none;
      padding: 0;
    }
  }

  blockquote {
    margin-left: 0;
    padding-left: 10px;
    border-left: 3px solid #cbd5e1;
    color: #64748b;
  }
`;

/** Пустое описание: подсказка, а не пустое место без объяснения. */
export const MarkdownEmpty = styled.div`
  font-size: 13px;
  color: #94a3b8;
`;

export const MdBar = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
  margin-bottom: 6px;
`;

export const MdButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 26px;
  height: 26px;
  padding: 0 6px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #fff;
  color: #475569;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: #cbd5e1;
    color: #0f172a;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

/** Разделитель групп кнопок: начертание | списки | вставка. */
export const MdSep = styled.span`
  width: 1px;
  height: 16px;
  background: #e2e8f0;
  margin: 0 2px;
`;

export const MdHint = styled.span`
  margin-left: auto;
  font-size: 11px;
  color: #94a3b8;
`;

/* ── Файлы (ТЗ 0066) ─────────────────────────────────────────────────────── */

/**
 * Зона файлов. Перетаскивание подсвечивается рамкой: без обратной связи
 * человек не понимает, отпускать здесь или нет.
 */
export const FileZone = styled.div<{ $over?: boolean }>`
  border: 1px dashed ${({ $over }) => ($over ? '#2563eb' : '#e2e8f0')};
  border-radius: 10px;
  padding: 8px;
  background: ${({ $over }) => ($over ? 'rgba(37, 99, 235, 0.04)' : '#fff')};
  transition: border-color 0.12s ease, background 0.12s ease;
`;

export const FileRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 4px;
  min-width: 0;

  & + & {
    border-top: 1px solid #f1f5f9;
  }
`;

export const FileName = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: none;
  padding: 0;
  font-size: 13px;
  color: #1e293b;
  text-align: left;
  cursor: pointer;
  min-width: 0;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:hover {
    color: #2563eb;
  }

  &:disabled {
    cursor: default;
    color: #94a3b8;
  }
`;

export const FileMeta = styled.span`
  font-size: 11px;
  color: #94a3b8;
  white-space: nowrap;
`;

/** Пометка «файл из обсуждения» — иначе непонятно, откуда он в списке. */
export const FileFrom = styled.span`
  font-size: 11px;
  color: #64748b;
  background: #f1f5f9;
  border-radius: 999px;
  padding: 1px 7px;
  white-space: nowrap;
`;

export const FileActions = styled.div`
  display: flex;
  gap: 6px;
  margin-left: auto;
`;

/** Развёрнутый предпросмотр картинки. Ссылка подписанная и живёт 5 минут. */
export const FilePreview = styled.img`
  display: block;
  max-width: 100%;
  max-height: 320px;
  margin: 6px 0 2px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
`;

/** Скрытое поле выбора файла — домашний приём (antd Upload в проекте нет). */
export const HiddenFileInput = styled.input`
  display: none;
`;
